/**
 * Org-admin Case Defaults section.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import type { OrgCaseDefaults, UpdateOrgCaseDefaultsRequest } from '../../types/case-container'
import { SettingCard } from './SettingCard'
import { EvidenceFormatField } from './EvidenceFormatField'
import { PathConfigSection } from './PathConfigSection'
import { DateTimeDefaultsSection } from './DateTimeDefaultsSection'

interface CaseDefaultsSectionProps {
  orgDefaults: OrgCaseDefaults | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  editing: boolean
  formData: UpdateOrgCaseDefaultsRequest
  setFormData: (data: UpdateOrgCaseDefaultsRequest) => void
  updating: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
}

function ButtonRow({
  editing,
  updating,
  onEdit,
  onCancel,
  onSave,
}: Pick<
  CaseDefaultsSectionProps,
  'editing' | 'updating' | 'onEdit' | 'onCancel' | 'onSave'
>): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex justify-end gap-3 border-t border-[var(--border)] pt-4">
      {editing ? (
        <>
          <button
            onClick={onCancel}
            className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--text-dim)] transition-colors hover:bg-[var(--accent-dim)]"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={onSave}
            disabled={updating}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {updating && <RefreshCw className="h-4 w-4 animate-spin" />}
            {t('adminSettings.saveDefaults')}
          </button>
        </>
      ) : (
        <button
          onClick={onEdit}
          className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          {t('adminSettings.editDefaults')}
        </button>
      )}
    </div>
  )
}

function CaseDefaultsBody(props: CaseDefaultsSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  if (props.loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-[var(--accent)]" />
      </div>
    )
  }
  if (props.error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{props.error}</p>
        <button
          onClick={() => {
            void props.refetch()
          }}
          className="mt-2 text-sm text-[var(--accent)] hover:underline"
        >
          {t('adminSettings.tryAgain')}
        </button>
      </div>
    )
  }
  return (
    <div className="space-y-6">
      <EvidenceFormatField
        orgDefaults={props.orgDefaults}
        formData={props.formData}
        setFormData={props.setFormData}
        editMode={props.editing}
      />
      <PathConfigSection
        orgDefaults={props.orgDefaults}
        formData={props.formData}
        setFormData={props.setFormData}
        editMode={props.editing}
      />
      <DateTimeDefaultsSection
        orgDefaults={props.orgDefaults}
        formData={props.formData}
        setFormData={props.setFormData}
        editMode={props.editing}
      />
      <ButtonRow
        editing={props.editing}
        updating={props.updating}
        onEdit={props.onEdit}
        onCancel={props.onCancel}
        onSave={props.onSave}
      />
    </div>
  )
}

export function CaseDefaultsSection(props: CaseDefaultsSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SettingCard
      title={t('adminSettings.defaultCaseSettings')}
      description={t('adminSettings.defaultCaseSettingsDesc')}
    >
      <CaseDefaultsBody {...props} />
    </SettingCard>
  )
}
